// Earth constants
export const EARTH_GEOMETRY = {
  RADIUS: 5,
  SEGMENTS: 32
};

export const EARTH_MATERIAL = {
  SHININESS: 5,
  SPECULAR: 0x333333
};

export const EARTH_POSITION = {
  X: -7,
  Y: 0,
  Z: 0
};

// Airplane constants
export const AIRPLANE = {
  SCALE: 0.5,
  POSITION: {
    HEIGHT: 7.0 // Just above earth's radius
  },
  TILT: 0.1,
  PROPELLER_ROTATION_SPEED: 0.3,
  ORBIT: {
    PATTERN: 'CIRCULAR', // Options: 'CIRCULAR', 'SINE_WAVE'
    LATITUDE_BASE: 30, // Base latitude for circular orbit
    ORBIT_TILT: 0, // Tilt angle of the orbit plane in degrees
    LONGITUDE_SPEED: 15, // Speed of forward motion
    RADIUS: 7.0,
    DIRECTION_AHEAD_VALUE: 2 // Look-ahead value for orientation
  },
  FUSELAGE: {
    DIAMETER_TOP: 0.3,
    DIAMETER_BOTTOM: 0.2,
    LENGTH: 2
  }
};

// LiDAR constants
export const LIDAR = {
  BASE: {
    RADIUS: 0.08,
    HEIGHT: 0.05
  },
  CONE: {
    RADIUS: 0.06,
    HEIGHT: 0.12,
    POSITION_Y: 0.08
  },
  SCANNING: {
    ANGLE_INCREMENT: 0.3,
    NUM_BEAMS: 24,
    CONE_ANGLE: 0.12
  },
  EARTH_INTERSECTION_RADIUS: EARTH_GEOMETRY.RADIUS // Match Earth radius for accurate scanning
};

// Stars constants
export const STARS = {
  COUNT: 5000,
  SIZE: 0.1,
  DISTRIBUTION_RANGE: 2000 // Stars will be distributed from -RANGE/2 to RANGE/2
};

// Scene constants
export const SCENE = {
  CONTAINER_HEIGHT_PERCENTAGE: 0.8, // 80% of screen height
  CAMERA: {
    FOV: 75,
    INITIAL_POSITION: {
      X: 0,
      Y: 5, // Level with the scene (ground level)
      Z: 5
    }
  },
  LIGHTS: {
    AMBIENT: 0x333333,
    DIRECTIONAL: {
      COLOR: 0xffffff,
      INTENSITY: 1,
      POSITION: {
        X: 5,
        Y: 3,
        Z: 5
      }
    }
  }
};

// Controls constants
export const CONTROLS = {
  ROTATION_SPEED_MULTIPLIER: 0.005,
  ZOOM: {
    SPEED: 0.01,
    MIN: 8,
    MAX: 50,
    INITIAL: 11 // Backed out further for a wider view
  }
};

// Global rotation speed
export const EARTH_ROTATION_SPEED = 0.002;

// Light Prism constants
export const LIGHT_PRISM = {
  RADIUS: 0.5,    // Width of the prism
  HEIGHT: 1.0,    // Height of the prism (length of the wedge)
  POSITION: {
    X: EARTH_POSITION.X ,  // Directly above Earth
    Y: EARTH_POSITION.Y + EARTH_GEOMETRY.RADIUS + 2,  // Higher than the Earth
    Z: EARTH_POSITION.Z
  },
  PRISM_COLOR: 0xFFFFFF,  // Crystal clear base color
  BEAM: {
    PARTICLE_COUNT: 200,  // More particles for a denser flow
    PARTICLE_SIZE: 0.12,  // Particle size
    PARTICLE_SPEED: 0.8,  // How fast particles move
    FLOW_WIDTH: 0.01        // How wide the particle flow is
  },
  // Second prism positioned to the right of the first
  SECOND_PRISM: {
    RADIUS: 0.5,
    HEIGHT: 1.0,
    POSITION: {
      X: EARTH_POSITION.X + 12,  // Farther to the right
      Y: EARTH_POSITION.Y + EARTH_GEOMETRY.RADIUS + 2,  // Same height as first prism
      Z: EARTH_POSITION.Z
    }
  },
  // Connecting beam between prisms
  CONNECTING_BEAM: {
    PARTICLE_COUNT: 150,
    PARTICLE_SIZE: 0.1,
    PARTICLE_SPEED: 0.6,
    FLOW_WIDTH: 0.05,
    COLOR: 0xFFFF00  // Yellow technicolor beam
  },
  // Terrain mesh settings
  TERRAIN: {
    POSITION: {
      X: EARTH_POSITION.X + 14,  // Move further to the right
      Y: EARTH_POSITION.Y + EARTH_GEOMETRY.RADIUS,  // Move lower for better visibility
      Z: EARTH_POSITION.Z
    },
    SIZE: 12,  // Size of the terrain
    RESOLUTION: 128,  // Increased mesh density for more detailed features
    MAX_HEIGHT: 10.0,  // Increased maximum height for more dramatic topography
    COLOR: 0x2E8B57,  // Base color of the terrain (vibrant green)
    HIGHLIGHT_COLOR: 0xFFFF00,  // Color for newly added vertices (bright yellow)
    WIREFRAME_COLOR: 0xFFFFFF,  // Color for the wireframe (white for visibility)
    // LAS point cloud specific settings
    POINT_CLOUD: {
      LAS_FILES: [
        '/lidar/dp2.las',        // Dana Point 2
        '/lidar/shinjuku2.las'
      ],  // List of available LAS files to load
      POINT_SIZE: 0.01,  // Point size for visibility
      POINT_BUDGET: 500000,  // For better performance
      COLOR_ENCODING: 'RGB', // Using RGB colors from the LAS file
      ENHANCE_COLORS: true,  // Apply color enhancement to make colors more vibrant
      COLOR_ADJUSTMENT: {    // Fine-tune color rendering
        GAMMA: 0.8,          // Gamma correction (lower = brighter)
        SATURATION: 1.3,     // Saturation multiplier (higher = more vivid)
        MIN_LIGHTNESS: 0.2,  // Minimum lightness (boost dark areas)
        MAX_LIGHTNESS: 0.9   // Maximum lightness (prevent washout)
      },
      ELEVATION_RANGE: {
        MIN: 0,
        MAX: 100
      },
      ELEVATION_GRADIENT: [
        { height: 0, color: 0x3366CC },  // Water/low areas - blue
        { height: 0.2, color: 0x44AA44 }, // Lowlands - light green
        { height: 0.4, color: 0x227722 }, // Hills - darker green
        { height: 0.7, color: 0x995533 }, // Mountains - brown
        { height: 1.0, color: 0xCCCCCC }  // Peaks - white/gray
      ]
    },
    LOADING: {
      POINTS_PER_FRAME: 1000,      // Number of points to add per frame
      INITIAL_DELAY: 1.0,         // Seconds to wait before loading
      TOTAL_DURATION: 5.0,        // Total seconds to complete loading all points
      RANDOM_ORDER: true,         // Load points in random order rather than sequential
      USE_WAVE_PATTERN: true,     // Use wave pattern for loading points (center first)
      WAVE_INTENSITY: 0.8,        // Wave intensity (0-1)
      JITTER_AMOUNT: 0.3,         // Random jitter amount (0-1)
      CHUNK_SIZE: 5000,           // Number of points to load in each chunk
      CHUNK_DELAY: 0.05,           // Delay between chunks in seconds
      ENABLE_LOADING_RAYS: true,  // Enable rays from prism to points during loading
      RAY_DURATION: 0.3,          // Duration of ray visibility in seconds
      RAY_COLOR: 0x00FFFF,        // Color of the loading rays
      RAY_WIDTH: 1.5,             // Width of the loading rays
      RAY_FREQUENCY: 20           // Show ray for every Nth point (higher = fewer rays)
    },
    PARTICLE_ABSORPTION: {
      RADIUS: 1.2,  // Slightly larger radius of influence
      STRENGTH: 0.4,  // Reduced strength for slower building
      FADE_SPEED: 0.92  // Slower fade for longer-lasting highlights
    },
    BUILD_SPEED: 0.05,  // Slowed down build speed as requested
    // Initial terrain shape with topographic features
    INITIAL_SHAPE: {
      ENABLED: true,
      NOISE_SCALE: 0.5,  // Increased for more varied terrain
      MIN_HEIGHT: 0.0,
      MAX_HEIGHT: 0.5  // Higher initial features
    },
    // Visual effects for impact
    VISUAL_EFFECTS: {
      ENABLE_PULSE: true,  // Enable a pulse effect when particles hit
      PULSE_STRENGTH: 0.2, // How much the pulse affects the terrain
      PULSE_DURATION: 0.3  // How long the pulse lasts in seconds
    },
    // Terrain feature types for different heights
    TERRAIN_FEATURES: {
      WATER_LEVEL: 0.2,      // Height below which is water
      BEACH_LEVEL: 0.4,      // Height for beach/shoreline
      GRASS_LEVEL: 1.0,      // Height for grassy areas
      MOUNTAIN_LEVEL: 2.0,   // Height for mountains
      PEAK_LEVEL: 2.8        // Height for mountain peaks
    }
  }
};